/* eslint-disable react/no-unstable-nested-components */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AxiosResponse } from "axios";
import styled from "styled-components";
import dayjs from "dayjs";
import { ViewState } from "@devexpress/dx-react-scheduler";
import {
	Scheduler,
	Appointments,
	MonthView,
	DateNavigator,
	Toolbar,
} from "@devexpress/dx-react-scheduler-material-ui";
import { MuiThemeProvider, createTheme } from "@material-ui/core/styles";

import { MAIN_THEME } from "styles/colors";
import PropertiesClient from "services/api/properties";
import ReportsClient from "services/api/reports";

import { CHANNEL_NAMES } from "interfaces/channels";

import { Property } from "models/properties";
import { defaultReservationsDateFilter } from "utils/reservations";
import { GenerateReportResponse } from "models/reports";
import { LoaderWithOverlay } from "components/atoms";
import PropertyMultiSelect from "components/molecules/PropertyMultiSelect";
import ReservationDetailsPanel from "./ReservationDetailsPanel";

const FilterContainer = styled.div`
	position: relative;
	z-index: 11;
	margin-bottom: 28px;
`;

const CalendarContainer = styled.div`
	position: relative;
	background: rgba(15, 26, 53, 0.8);
	border: 1px solid rgba(0, 217, 255, 0.15);
	border-radius: 16px;
	overflow: hidden;

	/* Improve calendar text readability */
	.dx-scheduler-table {
		color: ${MAIN_THEME.TEXT_PRIMARY};
	}

	.dx-scheduler-header-panel {
		background: rgba(15, 26, 53, 0.6);
		color: ${MAIN_THEME.TEXT_PRIMARY};
		border-bottom: 1px solid rgba(0, 217, 255, 0.1);
	}

	.dx-scheduler-cell {
		color: ${MAIN_THEME.TEXT_PRIMARY};
		border-color: rgba(0, 217, 255, 0.1);
	}

	.dx-scheduler-date-table-cell {
		background: rgba(15, 26, 53, 0.5) !important;
		color: #f8fafc !important;
	}

	.dx-scheduler-date-table-cell > div {
		color: #ffffff !important;
	}

	.dx-scheduler-date-table-cell > div > div {
		color: #ffffff !important;
	}

	.dx-scheduler-date-table-row {
		border-color: rgba(0, 217, 255, 0.1);
	}

	.dx-scheduler-month-view {
		background: transparent;
	}

	.dx-scheduler-date-header-cell {
		background: rgba(15, 26, 53, 0.6);
		color: #f8fafc !important;
		border-color: rgba(0, 217, 255, 0.1);
		font-weight: 600;
		font-size: 14px;
		letter-spacing: 0.3px;
	}

	/* Target date numbers specifically */
	.dx-scheduler-date-table-cell-text {
		color: #f8fafc !important;
		font-weight: 500;
		font-size: 13px;
	}

	/* Ensure all text in date cells is white */
	.dx-scheduler-date-table-cell * {
		color: #f8fafc !important;
	}

	/* Additional targeting for nested elements */
	.dx-scheduler-date-table-cell span {
		color: #f8fafc !important;
	}

	/* Target Material-UI TableCell root class */
	[class*="MuiTableCell-root"] {
		color: #f8fafc !important;
	}

	/* Target Material-UI generated classes in date table cells */
	.dx-scheduler-date-table-cell [class*="MuiTableCell"] {
		color: #f8fafc !important;
	}

	/* Target other month dates (previous/next month) */
	[class*="MuiTableCell-root"] .Cell-otherMonth {
		color: #ff6161 !important;
	}

	.dx-scheduler-time-panel-cell {
		color: ${MAIN_THEME.TEXT_SECONDARY};
	}

	/* Appointment styles */
	.dx-scheduler-appointment {
		opacity: 0.9;
	}

	/* Navigation and toolbar styles */
	.dx-scheduler-navigator {
		background: transparent;
	}

	.dx-button {
		color: ${MAIN_THEME.TEXT_PRIMARY};

		&:hover {
			background: rgba(0, 217, 255, 0.1);
		}
	}

	.dx-scheduler-toolbar {
		background: transparent;
	}
`;

const Container = styled.div`
	position: relative;
`;

const SchedulerFork = Scheduler as any;
// const AppointmentsFork = Appointments as any;

const theme = createTheme({
	palette: {
		type: "dark",
		primary: {
			main: MAIN_THEME.ACCEPT_COLOR,
		},
		secondary: {
			main: MAIN_THEME.ACCEPT_COLOR,
		},
		background: {
			paper: "rgba(15, 26, 53, 0.95)",
			default: MAIN_THEME.DARK_BG,
		},
		text: {
			primary: MAIN_THEME.TEXT_PRIMARY,
			secondary: MAIN_THEME.TEXT_SECONDARY,
		},
		divider: "rgba(0, 217, 255, 0.15)",
	},
	overrides: {
		MuiPaper: {
			root: {
				backgroundColor: "rgba(15, 26, 53, 0.95)",
				border: "1px solid rgba(0, 217, 255, 0.15)",
				boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
			},
		},
		MuiToolbar: {
			root: {
				backgroundColor: "rgba(15, 26, 53, 0.95)",
				borderBottom: "1px solid rgba(0, 217, 255, 0.1)",
			},
		},
		MuiIconButton: {
			root: {
				color: MAIN_THEME.ACCEPT_COLOR,
				"&:hover": {
					backgroundColor: "rgba(0, 217, 255, 0.12)",
				},
			},
			colorPrimary: {
				color: MAIN_THEME.ACCEPT_COLOR,
				"&:hover": {
					backgroundColor: "rgba(0, 217, 255, 0.12)",
					color: MAIN_THEME.ACCEPT_COLOR,
				},
			},
		},
		MuiButtonBase: {
			root: {
				"&:hover": {
					backgroundColor: "rgba(0, 217, 255, 0.12)",
				},
			},
		},
		MuiButton: {
			text: {
				"&:hover": {
					backgroundColor: "rgba(0, 217, 255, 0.12)",
				},
			},
		},
		MuiTypography: {
			body1: {
				color: MAIN_THEME.TEXT_PRIMARY,
			},
			body2: {
				color: MAIN_THEME.TEXT_PRIMARY,
			},
			h6: {
				color: MAIN_THEME.TEXT_PRIMARY,
				fontWeight: 600,
				letterSpacing: "0.3px",
			},
		},
		MuiTable: {
			root: {
				backgroundColor: "transparent",
			},
		},
		MuiTableHead: {
			root: {
				backgroundColor: "rgba(15, 26, 53, 0.6)",
			},
		},
		MuiTableCell: {
			root: {
				color: MAIN_THEME.TEXT_PRIMARY,
				borderColor: "rgba(0, 217, 255, 0.1)",
			},
			head: {
				color: MAIN_THEME.TEXT_SECONDARY,
				fontWeight: 600,
				fontSize: "13px",
				letterSpacing: "0.3px",
			},
			body: {
				color: MAIN_THEME.TEXT_PRIMARY,
				fontSize: "14px",
				padding: "12px 8px",
			},
		},
	},
});

const ReservationsCalendar = () => {
	const [properties, setProperties] = useState<any[]>([]);
	const [events, setEvents] = useState<any[]>([]);
	const [isLoading, setLoading] = useState(false);
	const [selectedProperties, setSelectedProperties] = useState<any[]>([
		{ label: "All", value: "" },
	]);
	const [selectedReservation, setSelectedReservation] = useState<any | null>(
		null
	);

	const onPageLoading = useCallback(
		async (date: Date | string, propertyId?: string) => {
			setLoading(true);
			setEvents([]);

			const dateFrom = dayjs(date).startOf("month").toISOString();
			const dateTo = dayjs(date).endOf("month").toISOString();

			try {
				const propertiesRes = (await PropertiesClient.getProperties({
					offset: 0,
					city: "",
					name: "",
				})) as AxiosResponse;

				const propertiesList = propertiesRes.data.content as Property[];

				if (propertiesList.length) {
					setProperties(propertiesList);
					const propertyIds = propertiesList.map((item) => item.id);
					const propertyIdsString = propertyIds.join(",");

					const propertiesMap = {} as {
						[n: string]: Property;
					};

					propertiesList.forEach((property) => {
						propertiesMap[property.id] = property;
					});

				// Build property IDs from selectedProperties
				let selectedPropertyIds = propertyIdsString;
				if (propertyId) {
					selectedPropertyIds = propertyId;
				} else if (selectedProperties?.length) {
					const selectedIds = selectedProperties
						.filter((p) => p.value)
						.map((p) => p.value);
					selectedPropertyIds = selectedIds.length ? selectedIds.join(",") : propertyIdsString;
				}

				const reportsRes = (await ReportsClient.generateReport({
					propertyIds: selectedPropertyIds,
					dateFrom,
					dateTo,
					dateFromType: defaultReservationsDateFilter.dateFromType,
					dateToType: defaultReservationsDateFilter.dateToType,
				})) as AxiosResponse<GenerateReportResponse>;

					const transformedReservations =
						reportsRes.data.content.calculations.allAccountTransfers.map(
							(item) => ({
								...item,
								property: propertiesMap[item._item.propertyId] || "",
							})
						);

					const updatedEvents = transformedReservations.map((reservation) => {
						const nameWithoutDash = (
							reservation?.property?.name || ""
						).replaceAll("-", " ");
						const nameWithoutUnderscore = nameWithoutDash.replaceAll("_", " ");

						let color = "rgb(255, 121, 38)";

						switch (reservation._item.channel) {
							case CHANNEL_NAMES.AIRBNB_OFFICIAL:
							case CHANNEL_NAMES.AIRBNB:
								color = "#FF385C";

								break;
							case CHANNEL_NAMES.BOOKING:
								color = "#02357F";

								break;
							default:
								color = "rgb(255, 121, 38)";

								break;
						}

						// ${nameWithoutUnderscore.slice(1)} ё${nameWithoutUnderscore
						// 	.charAt(0)
						// 	.toUpperCase()`}
						return {
							id: reservation._item.channel,
							title: `${reservation._item.guest.first_name} ${reservation._item.guest.last_name}`,
							startDate: reservation._item.start_date,
							endDate: reservation._item.end_date,
							color,
							reservation,
							channel: reservation._item.channel,
						};
					});

					setEvents(updatedEvents);
					setLoading(false);
				}
			} catch (error) {
				console.log({ error });
				setLoading(false);
			}
		},
		[selectedProperties]
	);

	useEffect(() => {
		onPageLoading(dayjs().toISOString());
	}, []);

	const transformedProperties = useMemo(
		() =>
			properties.map((item) => ({
				label: item.name,
				value: item.id,
			})),
		[properties]
	);

	const handleSelectReservation = (item: any | null) => {
		setSelectedReservation(item);
	};

	const handleCloseDetailsPanel = () => {
		setSelectedReservation(null);
	};

	const handleSelectProperties = (selectedProps: any[]) => {
		setSelectedProperties(selectedProps);
		onPageLoading(dayjs().toISOString());
	};

	const Appointment = ({ children, style, data, ...restProps }: any) => {
		return (
			<Appointments.Appointment
				{...restProps}
				onClick={() => handleSelectReservation(data.reservation)}
				style={{
					...style,
					backgroundColor: data.color,
					borderRadius: "8px",
				}}
			>
				{children}
			</Appointments.Appointment>
		);
	};

	// const AppointmentContent = (compProps: any) => {
	// 	const { data } = compProps;
	// 	const { color } = data;

	// 	return (
	// 		<Appointments.AppointmentContent
	// 			{...compProps}
	// 			style={{
	// 				width: "100%",
	// 				height: "100%",
	// 				backgroundColor: color,
	// 			}}
	// 		/>
	// 	);
	// };

	const renderReservationsDetailsPanel = () => {
		if (selectedReservation) {
			return (
				<ReservationDetailsPanel
					close={handleCloseDetailsPanel}
					hostServiceFee={`${selectedReservation._item.host_service_fee}`}
					ownerIncome={`${selectedReservation._item.owner_income}`}
					hoteliseHelp={`${selectedReservation._item.hotelise_help}`}
					payoutPrice={`${selectedReservation._item.payout_price}`}
					currency={selectedReservation._item.currency}
					propertyName={selectedReservation.property?.name || ""}
					propertyAddress={selectedReservation.property?.address?.display || ""}
					checkIn={selectedReservation._item.start_date}
					checkOut={selectedReservation._item.end_date}
					nights={selectedReservation._item.nights}
					guestsNumber={0}
					image={selectedReservation.property?.picture || ""}
					guest={selectedReservation._item.guest}
				/>
			);
		}
	};

		return (
			<Container>
				<FilterContainer>
					<PropertyMultiSelect
						properties={transformedProperties}
						selectedProperties={selectedProperties}
						onSelect={handleSelectProperties}
					/>
				</FilterContainer>
			<CalendarContainer>
				<MuiThemeProvider theme={theme}>
					<SchedulerFork
						data={events}
						style={{
							background: "transparent",
						}}
					>
						<ViewState onCurrentDateChange={onPageLoading} />
						<Toolbar />
						<DateNavigator />
						<MonthView />
						<Appointments
							appointmentComponent={Appointment}
							// appointmentContentComponent={AppointmentContent}
						/>
						{/* <AppointmentTooltip
							headerComponent={Header}
							contentComponent={Content}
							commandButtonComponent={CommandButton}
							showCloseButton
						/> */}
					</SchedulerFork>
				</MuiThemeProvider>
				{isLoading ? <LoaderWithOverlay /> : null}
			</CalendarContainer>
			{renderReservationsDetailsPanel()}
		</Container>
	);
};

export default ReservationsCalendar;
